import { db, collection, addDoc, serverTimestamp } from '../firebase/config';

/**
 * Create a notification for a user.
 * Writes to the top-level `notifications` collection with a `userId` field.
 */
export const createNotification = async ({
  userId,
  type,
  title,
  body,
  link = null,
  metadata = {},
}) => {
  if (!userId || !type || !title) {
    console.warn('createNotification: missing required fields', { userId, type, title });
    return null;
  }

  try {
    const ref = await addDoc(collection(db, 'notifications'), {
      userId,
      type,
      title,
      body: body || '',
      link,
      metadata,
      read: false,
      createdAt: serverTimestamp(),
    });
    return ref.id;
  } catch (err) {
    console.error('createNotification failed:', err);
    return null;
  }
};

/**
 * Icon name for a notification type (used by bell + notifications page).
 */
export const getNotificationIcon = (type) => {
  const map = {
    booking_reminder: 'calendar',
    booking_confirmed: 'check',
    booking_cancelled: 'x',
    booking_completed: 'star',
    booking_paid: 'credit-card',
    order_placed: 'package',
    order_shipped: 'truck',
    order_delivered: 'check',
    order_ready: 'store',
    message: 'message-circle',
    rating: 'star',
    system: 'bell',
    cart_gifted: 'gift',
    cart_shared: 'share',
    maintenance_paid: 'sparkles',
    maintenance_pay_request: 'link',
    checkout_gifted: 'gift',
  };
  return map[type] || 'bell';
};

const formatPrice = (n) => `R${Number(n || 0).toFixed(2)}`;

/**
 * Notify a user that someone paid for their shared cart.
 */
export const notifyCartGifted = async ({
  ownerId,
  gifterName,
  itemCount,
  orderId,
}) => {
  return createNotification({
    userId: ownerId,
    type: 'cart_gifted',
    title: 'Someone paid for your cart 🎁',
    body: `${gifterName} just checked out ${itemCount} item${itemCount === 1 ? '' : 's'} for you.`,
    link: '/dashboard/orders',
    metadata: { orderId, gifterName, itemCount },
  });
};

/**
 * Notify a user that someone paid for one of their maintenance routines.
 */
export const notifyMaintenancePaid = async ({
  ownerId,
  payerName,
  routineTitle,
  amount,
  scheduleId,
}) => {
  return createNotification({
    userId: ownerId,
    type: 'maintenance_paid',
    title: 'Your routine was paid for 💫',
    body: `${payerName} covered your "${routineTitle}" routine — R${Number(amount || 0).toFixed(2)}.`,
    link: '/dashboard',
    metadata: { scheduleId, payerName, amount },
  });
};

/**
 * Notify a user that someone paid for their whole checkout.
 */
export const notifyCheckoutGifted = async ({
  ownerId,
  payerName,
  total,
  itemCount,
  bookingCount,
}) => {
  const parts = [];
  if (itemCount > 0) parts.push(`${itemCount} item${itemCount === 1 ? '' : 's'}`);
  if (bookingCount > 0) parts.push(`${bookingCount} booking${bookingCount === 1 ? '' : 's'}`);

  return createNotification({
    userId: ownerId,
    type: 'checkout_gifted',
    title: 'Someone paid for your order 🎁',
    body: `${payerName} paid ${formatPrice(total)} for your order${parts.length ? ` (${parts.join(' + ')})` : ''}.`,
    link: '/dashboard/orders',
    metadata: { total, itemCount, bookingCount, payerName },
  });
};